import type { RateCard } from "@tdelivr/api-client/generated/pricing/pricing-configuration.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, windowPosition, type BoardRow, type DetailField } from "./board.ts";
import { money } from "./money.ts";

/**
 * What the pricing board does to the rate cards PRC-01 returns, away from any rendering.
 *
 * **This was the sharpest of the eleven absences and the listing closed it exactly.** The two reads
 * this service had were one version by `(rateCardId, version)` and the single card *effective* for
 * a `(productId, geographyId, effectiveAt)` triple — and a card awaiting approval is effective at
 * no instant, so no parameter would ever have returned the row the design's board leads with.
 * `GET /v1/tenants/{tenantId}/rate-cards` answers every version a tenant holds, newest first.
 *
 * **The `Scope` column is a product identifier and the design writes a product's name.** `all ride
 * services`, `delivery`, `Economy · Nairobi`. PRC-01 publishes `productId` and `geographyId` as
 * uuids and nothing else: a rate card does not carry what it is a rate card *for* in words. The
 * service that names a ride product is MOB-01, which is not an owner of this page, and **no service
 * on the platform publishes a name for a `geographyId` at all**. Q-102, whose recorded decision is
 * the identifier in Geist Mono with the grid saying it is an identifier rather than a name.
 *
 * **Money is written as the currency code and full minor units, as CLAUDE.md section 2 requires.**
 * The five components are integers in the minor unit of the `currency` published beside them, and
 * `money()` places the decimal point by splitting the integer's own decimal spelling rather than by
 * dividing by a hundred. The design's grid draws no money, so the components are in the detail
 * panel where the design puts a row's published fields — `KES 150.00`, not `150`.
 *
 * **The design's own note about the approval pattern has no enforcement behind it.** "The preparer
 * cannot approve their own rate card" — PRC-01's transition table is real and `approved` is the
 * only way into the published half of the lifecycle, so versioning and approval both exist. What
 * does not exist anywhere in that service is an actor: no `createdBy`, no `approvedBy`, no subject
 * on a version row. Four eyes cannot be enforced by a service that does not record the first pair.
 * Q-106.
 */

/**
 * The state class and the word.
 *
 * PRC-01's six statuses, each with the class the design uses for that kind of row, and the
 * contract's own word on the badge — the `zones.ts` rule: the published value is what the service
 * will say about this row tomorrow, and a prettier synonym chosen here is a second vocabulary
 * nobody else on the platform uses.
 *
 *   - `draft` is `idle`, the class whose label is "Not started".
 *   - `review` is `human`, and it is the one row on this board that tints. The design leads its own
 *     grid with `v13 · awaiting approval` under a solid attention badge for exactly this reason:
 *     a version in review is why somebody opened the page.
 *   - `approved` is `waiting` — "Waiting on others". The transition table admits `scheduled` and
 *     `active` only from here, so an approved card is waiting to be put into effect.
 *   - `scheduled` and `active` are the two the platform actually prices with, each qualified by its
 *     effective window below. Inside it, `scheduled` is `active` — the class whose label is "In
 *     progress" — and `active` is `done`.
 *   - `retired` is `closed`.
 *
 * **A card in the published half of the lifecycle and outside its effective window is `caution`,
 * not `done`.** PRC-01's own effective read — the operation a quote consults — selects
 *
 * ```sql
 * status IN ('active', 'scheduled') AND effective_from <= $4 AND (effective_to IS NULL OR effective_to > $4)
 * ```
 *
 * so a card stored `active` with an `effectiveTo` in the past prices nothing, and the design's
 * `done · live` on such a row would tell an administrator the fares a customer is being quoted come
 * from a card nothing is consulting. The same correction `zones.ts` makes against GEO-06, on a
 * service where the consequence is a price. The predicate is also why `scheduled` is tested against
 * the window at all: it is in the effective read, so a scheduled card whose window has opened is
 * already pricing.
 */
export function stateOf(
  card: RateCard,
  now: number,
): { classKey: StateClassKey; label: string; title?: string } {
  switch (card.status) {
    case "draft":
      return { classKey: "idle", label: "draft" };
    case "review":
      return {
        classKey: "human",
        label: "review",
        title:
          "Awaiting approval. PRC-01 admits `scheduled` and `active` only from `approved`, and `approved` only from here — but it records no actor on a version, so nothing enforces that the approver is a different person. Q-106.",
      };
    case "approved":
      return { classKey: "waiting", label: "approved" };
    case "retired":
      return { classKey: "closed", label: "retired" };
    case "scheduled":
    case "active": {
      const position = windowPosition(card.effectiveFrom, card.effectiveTo, now);
      if (position === "not_yet") {
        return {
          classKey: "caution",
          label: "not yet effective",
          title: `Stored ${card.status}, and effective from ${card.effectiveFrom}. PRC-01's effective read answers the version whose window contains the instant, so this card is not pricing anything yet.`,
        };
      }
      if (position === "ended") {
        return {
          classKey: "caution",
          label: "no longer effective",
          title: `Stored ${card.status}, and effective until ${card.effectiveTo ?? "an instant now past"}. PRC-01's effective read answers the version whose window contains the instant, so this card is not pricing anything any more.`,
        };
      }
      return card.status === "scheduled"
        ? {
            classKey: "active",
            label: "scheduled",
            title:
              "Stored scheduled and inside its effective window. PRC-01's effective read selects `status IN ('active','scheduled')`, so this card is already pricing.",
          }
        : { classKey: "done", label: "active" };
    }
  }
}

/**
 * The design's `Rate card` cell: the version, then the name.
 *
 * `v13 · fuel adjustment` is the design's own grammar for its leading row, and the version is first
 * because it is what an operator says out loud. A rate card holds many versions under one
 * `rateCardId` and the listing answers every one of them, so a cell showing the name alone would
 * draw the same words four times down the column.
 */
export function cardTitle(card: RateCard): string {
  return `v${String(card.version)} · ${card.name}`;
}

/**
 * The design's `Effective` cell.
 *
 * `from 1 Oct` for a window that has not opened and `1 Sep 2026` for one that has, which are the
 * design's own two forms. Its third, `from 1 Oct 00:00 EAT`, carries a time and a zone: no
 * operation on this platform publishes a tenant's time zone, so the day alone is drawn and Q-24
 * records the absence from the audit board's side.
 */
export function effectiveText(card: RateCard, now: number): string {
  const from = asDay(card.effectiveFrom);
  const opened = windowPosition(card.effectiveFrom, card.effectiveTo, now) !== "not_yet";
  const head = opened ? from : `from ${from}`;
  return card.effectiveTo === null ? head : `${head} – ${asDay(card.effectiveTo)}`;
}

/**
 * The five monetary components, each with the currency code and its full minor units.
 *
 * In the design's own field order from its `New rate card version` form — base fare, per km, per
 * minute, minimum fare — with `waitingMinorPerMinute` added because PRC-01 publishes it and the
 * form has no field for it. A published field left out of the one panel that lists published fields
 * would be the inverse of the error this console keeps finding.
 */
export function componentFields(card: RateCard): readonly DetailField[] {
  const at = (amount: number): string => money(amount, card.currency);
  return [
    { label: "Base fare", value: at(card.components.baseFareMinor) },
    { label: "Per kilometre", value: at(card.components.perDistanceMinorPerKilometre) },
    { label: "Per minute", value: at(card.components.perTimeMinorPerMinute) },
    { label: "Waiting, per minute", value: at(card.components.waitingMinorPerMinute) },
    { label: "Minimum fare", value: at(card.components.minimumFareMinor) },
  ];
}

/** One row of the pricing board. */
export function cardRow(card: RateCard, now: number): BoardRow {
  const state = stateOf(card, now);
  return {
    // A version is identified by its card and its number, and the listing answers both.
    id: `${card.rateCardId}/${String(card.version)}`,
    cells: [
      { text: cardTitle(card) },
      {
        text: card.productId,
        mono: true,
        title:
          "PRC-01 publishes the product this card prices as an identifier. The service that names a ride product is MOB-01, which this page does not read. Q-102.",
      },
      { text: effectiveText(card, now) },
    ],
    state,
    // The design tints `human`, and `review` is the only status that reaches it.
    ...(state.classKey === "human" ? { tinted: true } : {}),
    /*
     * Six lines where the design's panel lists three, and each of the six is here for a rule rather
     * than for completeness. Q-108 records the deviation and what it costs in pixels.
     *
     * **The five fares are CLAUDE.md section 2.** "Money is always shown with the currency code and
     * full minor units." PRC-01 publishes five integers and a currency on every row, and the design
     * draws money nowhere on this page — its only fares are in the `New rate card version` form,
     * which has no operation behind it. A pricing board that never writes a price would satisfy the
     * rule by vacuum.
     *
     * **`Geography` is the other half of Q-102.** The design's `Economy · Nairobi` is a product and
     * a place; the `Scope` column carries the product and this carries the place, because they are
     * two fields and one column.
     *
     * Everything else PRC-01 publishes is deliberately left out — `rateCardId`, `currency` (it is
     * in front of every amount), `roundingPolicy`, the three tax fields — because none of them is a
     * rule and each is a line of height the design does not have.
     */
    detail: [{ label: "Geography", value: card.geographyId, mono: true }, ...componentFields(card)],
    history: [
      { label: "Created", value: asDay(card.createdAt) },
      {
        label: "Approved",
        value: card.approvedAt === null ? "" : asDay(card.approvedAt),
        ...(card.approvedAt === null ? { absent: true } : {}),
      },
      { label: "Version", value: `v${String(card.version)}` },
    ],
    glyph: "sell",
  };
}

/**
 * The statuses the toolbar's filter offers, in PRC-01's own lifecycle order.
 *
 * All six, because the listing publishes a `status` query parameter and answers any of them — so
 * choosing one refetches rather than hiding rows, as the zones board's lifecycle filter does. That
 * is the opposite of the plan and configuration boards, and the difference is the contract's rather
 * than a preference: this listing publishes a filter and those do not.
 *
 * The two corrected readings — `not yet effective` and `no longer effective` — are deliberately
 * **not** options. They are a reading of `active` against a clock, not a value the service accepts,
 * and offering them would be offering a filter PRC-01 would answer 400 to.
 */
export const CARD_STATUSES: readonly RateCard["status"][] = [
  "draft",
  "review",
  "approved",
  "scheduled",
  "active",
  "retired",
];
